import { ReactNode, FC, useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { getParkingState } from 'src/store/parking/selectors';
import { parkingActions } from 'src/store/parking/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

import classes from './BottomSheetWrapper.module.scss';

export interface BottomSheetWrapperProps {
  title: string | undefined;
  action?: string;
  children?: ReactNode;
  containerClassName?: string;
  bodyClassName?: string;
  header?: ReactNode;
}

export const BottomSheetWrapper: FC<BottomSheetWrapperProps> = ({
  title,
  action,
  children,
  bodyClassName,
  containerClassName,
  header,
}) => {
  const { submit } = useForm();

  const dispatch = useAppDispatch();

  const { parkingBookingSheetIsOpened } = useSelector(getParkingState);

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(parkingActions.setParkingBookingSheetIsOpened(value));
    },
    [dispatch],
  );

  const handleClick = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    handleSetOpen(false);
  }, [handleSetOpen, submit]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    if (!action) return;

    return [
      {
        label: action,
        onClick: handleClick,
        variant: 'contained',
      },
    ];
  }, [action, handleClick]);

  return (
    <BottomSheet
      isOpen={parkingBookingSheetIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[0.5, 1]}
      className={classes.settings}
      initialSnapPoint={1}
      header={{
        icon: Icons.Pin,
        title: `Парковочное место ${title}`,
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={clsx(classes.container, containerClassName)}>
        {header}
        <div className={clsx(classes.body, bodyClassName)}>{children}</div>
      </div>
    </BottomSheet>
  );
};
